/**
 * Users Redux Sagas
 */

import { call, put, takeLatest, select } from 'redux-saga/effects'
import type { PayloadAction } from '@reduxjs/toolkit'
import { showErrorToast, showSuccessToast } from '@ufc/core'
import { usersApi } from '../infrastructure/api/users-api'
import { usersActions } from './users.slice'
import { selectUsersFilter, selectUsers } from './users.selectors'
import { selectAuthUser, canManageSystemUsers, isSystemUserRole } from '@ufc/domains/auth'
import type {
  User,
  UsersFilter,
  UsersListResponse,
  CreateUserRequest,
  UpdateUserRequest,
  ChangeUserRolePayload,
  BlockUserPayload,
  UnlockAttemptsPayload,
  ResetPasswordPayload,
} from '../domain/types'

function canCreateUsers(actorRoleType: string | null | undefined): boolean {
  return canManageSystemUsers(actorRoleType) || actorRoleType === 'customer'
}

function isCustomerOperator(actorRoleType: string | null | undefined): boolean {
  return actorRoleType === 'customer_operator'
}

function isSystemUser(user: User | undefined): boolean {
  if (!user) return false
  const roleType = user.strapiRole?.type ?? user.role
  return isSystemUserRole(roleType)
}

function* getTargetUser(targetUserId: string): Generator<any, User | undefined, any> {
  const users = (yield select(selectUsers)) as User[]
  return users.find((item) => item.id === targetUserId)
}

function* isRoleChangeOnNonSystemUser(targetUserId: string): Generator<any, boolean, any> {
  const targetUser = (yield* getTargetUser(targetUserId)) as User | undefined
  if (!targetUser) return false
  return !isSystemUser(targetUser)
}

function* isSystemUserMutationBlocked(targetUserId: string): Generator<any, boolean, any> {
  const authUser = (yield select(selectAuthUser)) as { role?: { type?: string | null } } | null
  const actorRoleType = authUser?.role?.type ?? null

  if (canManageSystemUsers(actorRoleType)) {
    return false
  }

  const targetUser = (yield* getTargetUser(targetUserId)) as User | undefined
  return isSystemUser(targetUser)
}

function* fetchUsersSaga(action: PayloadAction<UsersFilter | undefined>) {
  try {
    // Get current filter from state and merge with action payload
    const currentFilter: UsersFilter = yield select(selectUsersFilter)
    const filter = action.payload ? { ...currentFilter, ...action.payload } : currentFilter

    const response: UsersListResponse = yield call(usersApi.getUsers, filter)
    yield put(usersActions.fetchUsersSuccess(response))
  } catch (error) {
    yield put(usersActions.fetchUsersFailure((error as Error).message))
  }
}

function* createUserSaga(action: PayloadAction<CreateUserRequest>) {
  try {
    const authUser = (yield select(selectAuthUser)) as { role?: { type?: string | null } } | null
    const actorRoleType = authUser?.role?.type ?? null

    if (!canCreateUsers(actorRoleType)) {
      yield put(usersActions.createUserFailure('Access denied: you cannot add users'))
      return
    }

    if (actorRoleType === 'customer' && action.payload.strapiRoleType !== 'customer_operator') {
      yield put(
        usersActions.createUserFailure('Access denied: customers can only create customer operators')
      )
      return
    }

    const user: User = yield call(usersApi.createUser, action.payload)
    yield put(usersActions.createUserSuccess(user))
  } catch (error) {
    yield put(usersActions.createUserFailure((error as Error).message))
  }
}

function* updateUserSaga(action: PayloadAction<UpdateUserRequest>) {
  try {
    const authUser = (yield select(selectAuthUser)) as { role?: { type?: string | null } } | null
    const actorRoleType = authUser?.role?.type ?? null

    if (isCustomerOperator(actorRoleType)) {
      yield put(usersActions.updateUserFailure('Access denied: customer operators cannot manage users'))
      return
    }

    if (actorRoleType === 'customer') {
      const targetUser = (yield* getTargetUser(action.payload.userId)) as User | undefined
      const targetRoleType = targetUser?.strapiRole?.type ?? targetUser?.role
      if (targetRoleType !== 'customer_operator') {
        yield put(usersActions.updateUserFailure('Access denied: customers can only manage customer operators'))
        return
      }
    }

    if (action.payload.data.strapiRoleType) {
      const isNonSystemUserRoleChange: boolean = yield* isRoleChangeOnNonSystemUser(action.payload.userId)
      if (isNonSystemUserRoleChange) {
        yield put(
          usersActions.updateUserFailure('Access denied: non-system user roles cannot be changed')
        )
        return
      }
    }

    const isBlocked: boolean = yield* isSystemUserMutationBlocked(action.payload.userId)
    if (isBlocked) {
      yield put(usersActions.updateUserFailure('Access denied: cannot manage system users'))
      return
    }

    const user: User = yield call(usersApi.updateUser, action.payload.userId, action.payload.data)
    yield put(usersActions.updateUserSuccess(user))
  } catch (error) {
    yield put(usersActions.updateUserFailure((error as Error).message))
  }
}

function* changeUserRoleSaga(action: PayloadAction<ChangeUserRolePayload>) {
  try {
    const authUser = (yield select(selectAuthUser)) as { role?: { type?: string | null } } | null
    const actorRoleType = authUser?.role?.type ?? null

    if (isCustomerOperator(actorRoleType)) {
      yield put(usersActions.changeUserRoleFailure('Access denied: customer operators cannot manage users'))
      return
    }

    if (actorRoleType === 'customer') {
      yield put(usersActions.changeUserRoleFailure('Access denied: customers cannot change user roles'))
      return
    }

    const isNonSystemUserRoleChange: boolean = yield* isRoleChangeOnNonSystemUser(action.payload.userId)
    if (isNonSystemUserRoleChange) {
      yield put(
        usersActions.changeUserRoleFailure('Access denied: non-system user roles cannot be changed')
      )
      return
    }

    const isBlocked: boolean = yield* isSystemUserMutationBlocked(action.payload.userId)
    if (isBlocked) {
      yield put(usersActions.changeUserRoleFailure('Access denied: cannot manage system users'))
      return
    }

    const user: User = yield call(usersApi.changeUserRole, action.payload.userId, action.payload.role)
    yield put(usersActions.changeUserRoleSuccess(user))
  } catch (error) {
    yield put(usersActions.changeUserRoleFailure((error as Error).message))
  }
}

function* blockUserSaga(action: PayloadAction<BlockUserPayload>) {
  try {
    const authUser = (yield select(selectAuthUser)) as { role?: { type?: string | null } } | null
    const actorRoleType = authUser?.role?.type ?? null

    if (isCustomerOperator(actorRoleType)) {
      yield put(usersActions.blockUserFailure('Access denied: customer operators cannot manage users'))
      return
    }

    if (actorRoleType === 'customer') {
      const targetUser = (yield* getTargetUser(action.payload.userId)) as User | undefined
      const targetRoleType = targetUser?.strapiRole?.type ?? targetUser?.role
      if (targetRoleType !== 'customer_operator') {
        yield put(usersActions.blockUserFailure('Access denied: customers can only manage customer operators'))
        return
      }
    }

    const isBlocked: boolean = yield* isSystemUserMutationBlocked(action.payload.userId)
    if (isBlocked) {
      yield put(usersActions.blockUserFailure('Access denied: cannot manage system users'))
      return
    }

    const user: User = yield call(usersApi.blockUser, action.payload.userId, action.payload.blocked)
    yield put(usersActions.blockUserSuccess(user))
  } catch (error) {
    yield put(usersActions.blockUserFailure((error as Error).message))
  }
}

function* unlockAttemptsSaga(action: PayloadAction<UnlockAttemptsPayload>) {
  try {
    const user: User = yield call(usersApi.unlockAttempts, action.payload.userId)
    yield put(usersActions.unlockAttemptsSuccess(user))
    showSuccessToast('Account Unlocked', 'Login attempts have been cleared.')
  } catch (error) {
    const message = (error as Error).message ?? 'Failed to unlock account'
    yield put(usersActions.unlockAttemptsFailure(message))
    showErrorToast('Unlock Failed', message)
  }
}

function* resetUserPasswordSaga(action: PayloadAction<ResetPasswordPayload>) {
  try {
    const authUser = (yield select(selectAuthUser)) as { role?: { type?: string | null } } | null
    const actorRoleType = authUser?.role?.type ?? null

    if (isCustomerOperator(actorRoleType)) {
      yield put(usersActions.resetUserPasswordFailure('Access denied: customer operators cannot manage users'))
      return
    }

    if (actorRoleType === 'customer') {
      const targetUser = (yield* getTargetUser(action.payload.userId)) as User | undefined
      const targetRoleType = targetUser?.strapiRole?.type ?? targetUser?.role
      if (targetRoleType !== 'customer_operator') {
        yield put(
          usersActions.resetUserPasswordFailure(
            'Access denied: customers can only manage customer operators'
          )
        )
        return
      }
    }

    const isBlocked: boolean = yield* isSystemUserMutationBlocked(action.payload.userId)
    if (isBlocked) {
      yield put(usersActions.resetUserPasswordFailure('Access denied: cannot manage system users'))
      return
    }

    yield call(usersApi.resetPassword, action.payload.userId)
    yield put(usersActions.resetUserPasswordSuccess())
    showSuccessToast('Password Reset', 'Password reset email sent successfully.')
  } catch (error) {
    const message = (error as Error).message
    yield put(usersActions.resetUserPasswordFailure(message))
    showErrorToast('Reset Failed', message)
  }
}

// Re-fetch users when filter or pagination changes
function* refetchOnFilterChangeSaga() {
  yield put(usersActions.fetchUsers(undefined))
}

// Re-fetch users after successful create/update/block to reflect changes
function* refetchAfterMutationSaga() {
  yield put(usersActions.fetchUsers(undefined))
}

export function* usersSaga() {
  yield takeLatest(usersActions.fetchUsers.type, fetchUsersSaga)
  yield takeLatest(usersActions.createUser.type, createUserSaga)
  yield takeLatest(usersActions.updateUser.type, updateUserSaga)
  yield takeLatest(usersActions.changeUserRole.type, changeUserRoleSaga)
  yield takeLatest(usersActions.blockUser.type, blockUserSaga)
  yield takeLatest(usersActions.unlockAttempts.type, unlockAttemptsSaga)
  yield takeLatest(usersActions.resetUserPassword.type, resetUserPasswordSaga)

  // Re-fetch on filter/pagination changes
  yield takeLatest(usersActions.setFilter.type, refetchOnFilterChangeSaga)
  yield takeLatest(usersActions.setPage.type, refetchOnFilterChangeSaga)
  yield takeLatest(usersActions.setPageSize.type, refetchOnFilterChangeSaga)
  yield takeLatest(usersActions.filterByStatus.type, refetchOnFilterChangeSaga)
  yield takeLatest(usersActions.filterByRole.type, refetchOnFilterChangeSaga)
  yield takeLatest(usersActions.filterByTenant.type, refetchOnFilterChangeSaga)
  yield takeLatest(usersActions.searchUsers.type, refetchOnFilterChangeSaga)

  // Re-fetch after mutations to ensure list is current
  yield takeLatest(usersActions.createUserSuccess.type, refetchAfterMutationSaga)
  yield takeLatest(usersActions.updateUserSuccess.type, refetchAfterMutationSaga)
  yield takeLatest(usersActions.blockUserSuccess.type, refetchAfterMutationSaga)
  yield takeLatest(usersActions.unlockAttemptsSuccess.type, refetchAfterMutationSaga)
  yield takeLatest(usersActions.changeUserRoleSuccess.type, refetchAfterMutationSaga)
}
